import React, { useState } from 'react';
import { Download, Upload, Database, AlertCircle, CheckCircle, Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { apiClient } from '../../utils/apiHelper';

const DataMigration = () => {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);
  const [error, setError] = useState(null);

  // NEW: Migrate payments to fee_payments collection
  const handleMigratePayments = async () => {
    setMigrating(true);
    setError(null);
    setMigrationResult(null);
    
    try {
      const response = await apiClient.post('/api/data-migration/migrate-to-fee-payments');
      setMigrationResult(response.data);
    } catch (err) {
      setError('Migration failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMigrating(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    setExportResult(null);
    
    try {
      const response = await apiClient.get('/api/data-migration/export/full-tenant-data');
      const data = response.data;
      
      // Create and download JSON file
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `school_erp_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setExportResult({
        success: true,
        totalRecords: data.export_info?.total_records || 0,
        collectionsExported: data.export_info?.collections_exported || 0,
        exportedAt: data.export_info?.exported_at
      });
    } catch (err) {
      setError('Export failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setImporting(true);
    setError(null);
    setImportResult(null);
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      const response = await apiClient.post('/api/data-migration/import/full-tenant-data', data);
      setImportResult(response.data);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON file format');
      } else {
        setError('Import failed: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Data Migration</h1>
        <p className="text-gray-600 mt-1">Export and import all your school data as JSON file</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="font-medium text-red-800">Error</p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Fee Payments Migration Card - NEW */}
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-600" />
              Fee Payments Migration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Migrate all payment records to unified fee_payments collection for Student Portal.
            </p>
            <ul className="text-sm text-gray-500 mb-4 space-y-1">
              <li>• Seat Booking Payments</li>
              <li>• Admission Fees</li>
              <li>• Hostel Fees & Damages</li>
              <li>• Transport Fees</li>
              <li>• Library Fines</li>
              <li>• Other Fee Payments</li>
              <li>• Caution Money</li>
            </ul>
            
            <Button 
              onClick={handleMigratePayments} 
              disabled={migrating}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="migrate-payments-btn"
            >
              {migrating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Migrating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Migrate Payments
                </>
              )}
            </Button>

            {migrationResult && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Migration Completed!
                </div>
                <div className="text-sm text-green-600 mt-2 space-y-1">
                  {migrationResult.results && (
                    <>
                      <p>✓ Seat Bookings: {migrationResult.results.seat_bookings_migrated || 0} migrated, {migrationResult.results.seat_bookings_skipped || 0} skipped</p>
                      <p>✓ Admission Fees: {migrationResult.results.admission_fees_migrated || 0} migrated, {migrationResult.results.admission_fees_skipped || 0} skipped</p>
                      <p>✓ Hostel Fees: {migrationResult.results.hostel_fees_migrated || 0} migrated</p>
                      <p>✓ Transport Fees: {migrationResult.results.transport_fees_migrated || 0} migrated</p>
                      <p>✓ Other Fees: {migrationResult.results.other_fees_migrated || 0} migrated</p>
                      <p>✓ Library Fines: {migrationResult.results.library_fines_migrated || 0} migrated</p>
                      <p>✓ Caution Money: {migrationResult.results.caution_money_migrated || 0} migrated</p>
                    </>
                  )}
                  {migrationResult.results?.errors?.length > 0 && (
                    <p className="text-red-600">Errors: {migrationResult.results.errors.length}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-blue-600" />
              Export Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Download all your school data as a JSON file. This includes:
            </p>
            <ul className="text-sm text-gray-500 mb-4 space-y-1">
              <li>• Students, Staff & Users</li>
              <li>• Classes, Sections & Subjects</li>
              <li>• Fee Payments & Structures</li>
              <li>• Attendance Records</li>
              <li>• Examination & Results</li>
              <li>• Library Books & Issues</li>
              <li>• Inventory Items & Purchases</li>
              <li>• Hostel & Transport Data</li>
              <li>• Accounts & Transactions</li>
              <li>• Front Desk Records</li>
              <li>• HR & Payroll Data</li>
              <li>• All Settings</li>
            </ul>
            
            <Button 
              onClick={handleExport} 
              disabled={exporting}
              className="w-full"
              data-testid="export-data-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download JSON File
                </>
              )}
            </Button>

            {exportResult && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Export Successful!
                </div>
                <div className="text-sm text-green-600 mt-1">
                  <p>Total Records: {exportResult.totalRecords}</p>
                  <p>Collections: {exportResult.collectionsExported}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Import Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              Import Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Upload a previously exported JSON file to restore your data.
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-yellow-800 text-sm font-medium">⚠️ Important Notes:</p>
              <ul className="text-yellow-700 text-sm mt-1 space-y-1">
                <li>• Duplicate records (same ID) will be skipped</li>
                <li>• New records will be added to existing data</li>
                <li>• Make sure to backup before importing</li>
              </ul>
            </div>

            <label className="block">
              <input
                type="file"
                accept=".json"
                onChange={handleImport}
                disabled={importing}
                className="hidden"
                data-testid="import-file-input"
              />
              <div className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                importing ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
              }`}>
                {importing ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    <p className="text-gray-600">Importing data...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Database className="w-8 h-8 text-gray-400" />
                    <p className="text-gray-600">Click to select JSON file</p>
                    <p className="text-gray-400 text-sm">or drag and drop</p>
                  </div>
                )}
              </div>
            </label>

            {importResult && (
              <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="w-4 h-4" />
                  Import Completed!
                </div>
                <div className="text-sm text-green-600 mt-1">
                  <p>Total Imported: {importResult.total_imported}</p>
                  <p>Skipped (duplicates): {importResult.total_skipped}</p>
                  {importResult.errors?.length > 0 && (
                    <p className="text-yellow-600">Errors: {importResult.errors.length}</p>
                  )}
                </div>
                
                {/* Show collection details */}
                {importResult.collections && Object.keys(importResult.collections).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-green-200">
                    <p className="text-xs font-medium text-green-700 mb-1">Details by Collection:</p>
                    <div className="max-h-32 overflow-y-auto text-xs">
                      {Object.entries(importResult.collections).map(([name, stats]) => (
                        stats.imported > 0 && (
                          <div key={name} className="flex justify-between text-green-600">
                            <span>{name}</span>
                            <span>+{stats.imported}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DataMigration;
